import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface GeographyData {
  state: string;
  zipCode: string;
  roomCount: number;
  totalIssues: number;
  high: number;
  medium: number;
  low: number;
}

interface GeographicBreakdownTableProps {
  data: GeographyData[] | null;
  isLoading?: boolean;
}

export const GeographicBreakdownTable: React.FC<GeographicBreakdownTableProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Geographic Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const tableData = data ?? [];

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-foreground flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Geographic Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent>
        {tableData.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No geographic data available
          </div>
        ) : (
          <div className="max-h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>State</TableHead>
                  <TableHead>Zip Code</TableHead>
                  <TableHead className="text-center">Rooms</TableHead>
                  <TableHead className="text-center">Total Issues</TableHead>
                  <TableHead className="text-center">High</TableHead>
                  <TableHead className="text-center">Medium</TableHead>
                  <TableHead className="text-center">Low</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.map((row, index) => (
                  <TableRow key={`${row.state}-${row.zipCode}-${index}`}>
                    <TableCell className="font-medium">{row.state}</TableCell>
                    <TableCell>{row.zipCode}</TableCell>
                    <TableCell className="text-center">{row.roomCount}</TableCell>
                    <TableCell className="text-center font-semibold">{row.totalIssues}</TableCell>
                    <TableCell className="text-center">
                      {row.high > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {row.high}
                        </Badge>
                      )}
                      {row.high === 0 && <span className="text-muted-foreground">0</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {row.medium > 0 && (
                        <Badge className="bg-orange-500 hover:bg-orange-600 text-xs">
                          {row.medium}
                        </Badge>
                      )}
                      {row.medium === 0 && <span className="text-muted-foreground">0</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {row.low > 0 && (
                        <Badge className="bg-green-500 hover:bg-green-600 text-xs">
                          {row.low}
                        </Badge>
                      )}
                      {row.low === 0 && <span className="text-muted-foreground">0</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
